/**
 * Simplified Business Hours Management
 * カレンダーベースの予約枠管理UI
 *
 * サロンボードスタイルのシンプルな営業時間設定
 */

import React, { useState, useEffect, memo } from 'react';
import { businessHoursService } from '../../../services/businessHoursService';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  NavigateBefore,
  NavigateNext,
  AccessTime as TimeIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

interface SimplifiedBusinessHoursTabProps {
  settings?: Record<string, unknown>;
  updateSettings?: (section: string, updates: Record<string, unknown>) => Promise<void>;
}

interface DaySchedule {
  date: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  isHoliday: boolean;
  holidayName?: string;
  reservationSlots?: number;
}

interface CalendarDay {
  date: Date;
  schedule: DaySchedule | null;
  isCurrentMonth: boolean;
  isToday: boolean;
}

const SimplifiedBusinessHoursTab: React.FC<SimplifiedBusinessHoursTabProps> = ({ settings, updateSettings }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State管理
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<DaySchedule | null>(null);

  // デフォルト営業時間（storeから初期化）
  const [defaultSchedule, setDefaultSchedule] = useState(() => {
    const store = settings?.store as Record<string, unknown> | undefined;
    const businessHours = store?.businessHours;
    if (businessHours && typeof businessHours === 'object') {
      return businessHours as Record<string, unknown>;
    }
    return {
      monday: { isOpen: false, openTime: '10:00', closeTime: '20:00' },
      tuesday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      wednesday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      thursday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      friday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      saturday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      sunday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
    };
  });

  // カレンダー初期化
  useEffect(() => {
    generateCalendarDays();
  }, [currentMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // 月の最初の日と最後の日を取得
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // カレンダーの開始日（前月の日曜日から）
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // カレンダーの終了日（翌月の土曜日まで）
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const date = new Date(d);
      const dayOfWeek = date.getDay();
      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
      const defaultDaySchedule = defaultSchedule[dayName as keyof typeof defaultSchedule] as { isOpen: boolean; openTime: string; closeTime: string };

      days.push({
        date: new Date(d),
        schedule: {
          date: date.toISOString().split('T')[0],
          isOpen: defaultDaySchedule?.isOpen || false,
          openTime: defaultDaySchedule?.openTime || '10:00',
          closeTime: defaultDaySchedule?.closeTime || '20:00',
          isHoliday: !defaultDaySchedule?.isOpen,
          reservationSlots: defaultDaySchedule?.isOpen ? 8 : 0,
        },
        isCurrentMonth: date.getMonth() === month,
        isToday: date.getTime() === today.getTime(),
      });
    }

    setCalendarDays(days);
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDayClick = (day: CalendarDay) => {
    const dateStr = day.date.toISOString().split('T')[0];

    if (selectedDays.includes(dateStr)) {
      setSelectedDays(selectedDays.filter(d => d !== dateStr));
    } else {
      setSelectedDays([...selectedDays, dateStr]);
    }
  };

  const handleBatchEdit = () => {
    if (selectedDays.length === 0) return;

    // 選択した日付の最初のスケジュールを編集用に設定
    const firstDay = calendarDays.find(d =>
      selectedDays.includes(d.date.toISOString().split('T')[0])
    );

    if (firstDay?.schedule) {
      setEditingSchedule(firstDay.schedule);
      setEditDialogOpen(true);
    }
  };

  const handleSaveSchedule = async () => {
    if (!editingSchedule) return;

    // 選択した日付にスケジュールを適用
    const updatedDays = calendarDays.map(day => {
      const dateStr = day.date.toISOString().split('T')[0];
      if (selectedDays.includes(dateStr)) {
        return {
          ...day,
          schedule: {
            ...editingSchedule,
            date: dateStr,
          },
        };
      }
      return day;
    });

    setCalendarDays(updatedDays);

    // 選択した日付の曜日を取得してdefaultScheduleを更新
    const updatedDefaultSchedule = { ...defaultSchedule };
    selectedDays.forEach(dateStr => {
      const day = updatedDays.find(d => d.date.toISOString().split('T')[0] === dateStr);
      if (day) {
        const dayOfWeek = day.date.getDay();
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
        updatedDefaultSchedule[dayName] = {
          isOpen: editingSchedule.isOpen,
          openTime: editingSchedule.openTime,
          closeTime: editingSchedule.closeTime,
        };
      }
    });

    setDefaultSchedule(updatedDefaultSchedule);

    // Storeに保存
    if (updateSettings) {
      await updateSettings('store', {
        businessHours: updatedDefaultSchedule
      });
      // businessHoursServiceをリフレッシュ
      businessHoursService.refresh();
    }

    setEditDialogOpen(false);
    setSelectedDays([]);
    setEditingSchedule(null);
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const monthNames = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ];

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          営業時間・予約枠設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          カレンダーから日付を選択して営業時間を設定できます
        </Typography>
      </Box>

      {/* 情報アラート */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          backgroundColor: alpha('#2196f3', 0.08),
          '& .MuiAlert-icon': { color: 'info.main' }
        }}
      >
        複数日を選択して一括設定も可能です。営業時間と予約可能枠数を簡単に管理できます。
      </Alert>

      {/* ツールバー */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousMonth}>
              <NavigateBefore />
            </IconButton>
            <Typography variant="h6">
              {currentMonth.getFullYear()}年 {monthNames[currentMonth.getMonth()]}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <NavigateNext />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {selectedDays.length > 0 && (
              <>
                <Chip
                  label={`${selectedDays.length}日選択中`}
                  color="primary"
                  onDelete={() => setSelectedDays([])}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleBatchEdit}
                >
                  編集
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* カレンダー */}
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {/* 曜日ヘッダー */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            background: '#e0e0e0',
            border: '1px solid #e0e0e0',
            mb: 2,
          }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: index === 0 ? 'error.main' : index === 6 ? 'info.main' : 'text.primary',
                  backgroundColor: 'background.paper',
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* カレンダー日付 */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            background: '#e0e0e0',
            border: '1px solid #e0e0e0',
          }}>
            {calendarDays.map((day, index) => {
              const dateStr = day.date.toISOString().split('T')[0];
              const isSelected = selectedDays.includes(dateStr);
              const dayOfWeek = day.date.getDay();

              return (
                <Box
                key={index}
                onClick={() => handleDayClick(day)}
                sx={{
                  minHeight: isMobile ? 100 : 120,
                  cursor: 'pointer',
                  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.15) :
                                 !day.isCurrentMonth ? '#fafafa' :
                                 day.isToday ? alpha(theme.palette.info.main, 0.04) :
                                 day.schedule?.isHoliday ? '#f5f5f5' :
                                 'background.paper',
                  opacity: day.isCurrentMonth ? 1 : 0.5,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  '&:hover': {
                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.2) :
                                   day.schedule?.isHoliday ? '#eeeeee' :
                                   alpha(theme.palette.action.hover, 0.08),
                    boxShadow: isSelected ? '0 4px 12px rgba(25,118,210,0.3)' : '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1,
                  },
                  ...(isSelected && {
                    boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                    zIndex: 2,
                  }),
                  ...(day.isToday && {
                    boxShadow: '0 2px 8px rgba(33,150,243,0.2)',
                  }),
                }}
              >
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* 日付表示 */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: day.isToday ? 700 : 500,
                          color: dayOfWeek === 0 ? 'error.main' :
                                 dayOfWeek === 6 ? 'info.main' :
                                 'text.primary',
                          fontSize: isMobile ? '0.875rem' : '1rem'
                        }}
                      >
                        {day.date.getDate()}
                      </Typography>
                      {day.isToday && (
                        <TodayIcon sx={{ fontSize: 16, color: 'info.main' }} />
                      )}
                    </Box>

                    {/* 営業時間・予約枠表示 */}
                    {day.schedule && day.isCurrentMonth && (
                      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {day.schedule.isOpen ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <TimeIcon sx={{ fontSize: 12, mr: 0.5 }} />
                              {formatTime(day.schedule.openTime)}-{formatTime(day.schedule.closeTime)}
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: alpha(theme.palette.success.main, 0.1),
                                color: 'success.dark',
                                borderRadius: '8px',
                                minHeight: isMobile ? '36px' : '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.875rem' : '1rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                                  transform: 'translateY(-1px)',
                                },
                              }}
                            >
                              {day.schedule.reservationSlots}枠
                            </Box>
                          </>
                        ) : (
                          <Box
                            sx={{
                              backgroundColor: 'transparent',
                              color: '#666',
                              borderRadius: '8px',
                              minHeight: isMobile ? '36px' : '48px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 500,
                              fontSize: isMobile ? '0.875rem' : '1rem',
                              border: '1px dashed',
                              borderColor: 'divider',
                            }}
                          >
                            定休日
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* 凡例 */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            凡例
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <Chip
              icon={<CheckIcon />}
              label="営業日"
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<CancelIcon />}
              label="定休日"
              color="error"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<TodayIcon />}
              label="今日"
              color="info"
              size="small"
              variant="outlined"
            />
            <Chip
              label="選択中"
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>

      {/* 編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          営業時間設定
          {selectedDays.length > 1 && (
            <Chip
              label={`${selectedDays.length}日を一括編集`}
              size="small"
              color="primary"
              sx={{ ml: 'auto' }}
            />
          )}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {editingSchedule && (
            <Box sx={{ pt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingSchedule.isOpen}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      isOpen: e.target.checked,
                      isHoliday: !e.target.checked,
                    })}
                  />
                }
                label="営業日"
                sx={{ mb: 3 }}
              />

              {editingSchedule.isOpen && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    営業時間
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                      label="開店時間"
                      type="time"
                      fullWidth
                      variant="outlined"
                      value={editingSchedule.openTime}
                      onChange={(e) => setEditingSchedule({
                        ...editingSchedule,
                        openTime: e.target.value,
                      })}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <TimeIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                    <TextField
                      label="閉店時間"
                      type="time"
                      fullWidth
                      variant="outlined"
                      value={editingSchedule.closeTime}
                      onChange={(e) => setEditingSchedule({
                        ...editingSchedule,
                        closeTime: e.target.value,
                      })}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <TimeIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    予約設定
                  </Typography>
                  <TextField
                    label="予約可能枠数"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={editingSchedule.reservationSlots || 8}
                    onChange={(e) => setEditingSchedule({
                      ...editingSchedule,
                      reservationSlots: parseInt(e.target.value) || 0,
                    })}
                    helperText="1日の最大予約受付数を設定します"
                    inputProps={{ min: 0, max: 50 }}
                  />
                </Box>
              )}

              {!editingSchedule.isOpen && (
                <TextField
                  label="休業理由（任意）"
                  fullWidth
                  value={editingSchedule.holidayName || ''}
                  onChange={(e) => setEditingSchedule({
                    ...editingSchedule,
                    holidayName: e.target.value,
                  })}
                  placeholder="定休日、臨時休業など"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            startIcon={<CheckIcon />}
          >
            {selectedDays.length > 1 ? `${selectedDays.length}日分を保存` : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(SimplifiedBusinessHoursTab);